"use client";

import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "@/lib/auth";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

const DARK = "#003d45";

// ── Types ──────────────────────────────────────────────────────────────────

interface AnswerItem {
    question_id?: string;
    prompt: string;
    answer_type: string;
    value: unknown;
}

interface Submission {
    id: string;
    user_name: string | null;
    user_email: string | null;
    user_role: string | null;
    page_category: string;
    page_path: string | null;
    is_bug: boolean;
    message: string | null;
    answers: AnswerItem[] | null;
    created_at: string;
}

interface Insights {
    total: number;
    bugs: number;
    ideas: number;
    per_category: { page_category: string; count: number }[];
    avg_rating_per_category: { page_category: string; avg_rating: number }[];
}

interface Question {
    id: string;
    page_category: string;
    prompt: string;
    answer_type: string;
    options?: string[] | null;
    sort_order: number;
    is_active: boolean;
}

type Tab = "submissions" | "questions";

// ── Component ──────────────────────────────────────────────────────────────

export function FeedbackPanel() {
    const [tab, setTab] = useState<Tab>("submissions");
    const { replay: replayTour } = useAutoTour("staff.feedback", TOURS["staff.feedback"]);

    return (
        <div className="space-y-6">
            <div id="feedback-tabs" className="flex items-center gap-2 border-b border-[#e2e8f0]">
                {(["submissions", "questions"] as Tab[]).map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`px-4 py-2.5 text-sm font-bold transition-colors ${
                            tab === t
                                ? "border-b-2 border-[#007a8a] text-[#003d45]"
                                : "text-[#888] hover:text-[#003d45]"
                        }`}
                    >
                        {t === "submissions" ? "Submissions" : "Manage Questions"}
                    </button>
                ))}
                <span className="ml-auto pb-1"><TourButton onClick={replayTour} /></span>
            </div>

            {tab === "submissions" ? <SubmissionsTab /> : <QuestionsTab />}
        </div>
    );
}

// ── Submissions tab ────────────────────────────────────────────────────────

function SubmissionsTab() {
    const [insights, setInsights] = useState<Insights | null>(null);
    const [subs, setSubs] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("");
    const [bugsOnly, setBugsOnly] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (category) params.set("page_category", category);
            if (bugsOnly) params.set("is_bug", "true");
            const [iRes, sRes] = await Promise.all([
                fetchWithAuth(`/api/v1/feedback/insights`),
                fetchWithAuth(`/api/v1/feedback/submissions?${params.toString()}`),
            ]);
            if (iRes.ok) setInsights(await iRes.json());
            if (sRes.ok) setSubs(await sRes.json());
        } finally {
            setLoading(false);
        }
    }, [category, bugsOnly]);

    useEffect(() => { load(); }, [load]);

    const categories = insights?.per_category.map((c) => c.page_category) || [];

    return (
        <div className="space-y-6">
            {/* Insight cards */}
            {insights && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard label="Total" value={insights.total} icon="forum" />
                    <StatCard label="Bugs" value={insights.bugs} icon="bug_report" color="#dc2626" />
                    <StatCard label="Ideas / Praise" value={insights.ideas} icon="lightbulb" />
                    <StatCard
                        label="Top Page"
                        value={insights.per_category[0]?.page_category || "—"}
                        icon="trending_up"
                        isText
                    />
                </div>
            )}

            {/* Avg ratings */}
            {insights && insights.avg_rating_per_category.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {insights.avg_rating_per_category.map((r) => (
                        <span
                            key={r.page_category}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#f0f2f4] px-3 py-1.5 text-xs font-semibold text-[#444]"
                        >
                            <span className="capitalize">{r.page_category}</span>
                            <span className="font-black text-[#007a8a]">★ {r.avg_rating}</span>
                        </span>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm"
                >
                    <option value="">All pages</option>
                    {categories.map((c) => (
                        <option key={c} value={c} className="capitalize">{c}</option>
                    ))}
                </select>
                <label className="flex items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                        type="checkbox"
                        checked={bugsOnly}
                        onChange={(e) => setBugsOnly(e.target.checked)}
                        className="h-4 w-4 rounded text-[#007a8a]"
                    />
                    Bugs only
                </label>
            </div>

            {/* List */}
            {loading ? (
                <p className="text-sm text-[#888]">Loading…</p>
            ) : subs.length === 0 ? (
                <p className="text-sm text-[#888]">No feedback yet.</p>
            ) : (
                <div className="space-y-3">
                    {subs.map((s) => (
                        <div key={s.id} className="rounded-xl border border-[#e2e8f0] bg-white p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-[#1a1a1a]">
                                        {s.user_name || s.user_email || "Unknown"}
                                        {s.user_role && (
                                            <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-[#007a8a]">
                                                {s.user_role}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-[#888]">
                                        <span className="capitalize">{s.page_category}</span>
                                        {s.page_path && ` · ${s.page_path}`} · {new Date(s.created_at).toLocaleString()}
                                    </p>
                                </div>
                                {s.is_bug && (
                                    <span className="flex-shrink-0 rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-[#dc2626]">
                                        🐞 Bug
                                    </span>
                                )}
                            </div>

                            {s.answers && s.answers.length > 0 && (
                                <div className="mt-3 space-y-1.5">
                                    {s.answers.map((a, i) => (
                                        <div key={i} className="text-sm">
                                            <span className="text-[#666]">{a.prompt}</span>
                                            <span className="ml-2 font-semibold text-[#1a1a1a]">
                                                {String(a.value)}
                                                {a.answer_type === "rating" && " / 5"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {s.message && (
                                <p className="mt-3 rounded-lg bg-[#f8fafc] p-3 text-sm text-[#333] italic">
                                    “{s.message}”
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, icon, color = DARK, isText = false }: {
    label: string; value: number | string; icon: string; color?: string; isText?: boolean;
}) {
    return (
        <div className="rounded-xl border border-[#e2e8f0] bg-white p-4">
            <div className="flex items-center gap-2 text-[#888]">
                <span className="material-symbols-outlined text-[18px]" style={{ color }}>{icon}</span>
                <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
            </div>
            <p className={`mt-1.5 font-black text-[#1a1a1a] ${isText ? "text-lg capitalize" : "text-2xl"}`}>{value}</p>
        </div>
    );
}

// ── Questions tab ──────────────────────────────────────────────────────────

type AnswerType = "rating" | "emoji" | "text" | "choice";

interface QuestionForm {
    page_category: string;
    prompt: string;
    answer_type: AnswerType;
    options: string[];
    is_active: boolean;
}

const BLANK_FORM: QuestionForm = {
    page_category: "general",
    prompt: "",
    answer_type: "rating",
    options: [],
    is_active: true,
};

// Known pages with friendly labels + icons. Staff can also type a custom one.
const PAGE_PRESETS: { value: string; label: string; icon: string }[] = [
    { value: "general",    label: "Every page (fallback)", icon: "public" },
    { value: "dashboard",  label: "Dashboard",  icon: "dashboard" },
    { value: "groups",     label: "Groups",     icon: "groups" },
    { value: "events",     label: "Events",     icon: "event" },
    { value: "volunteers", label: "Volunteers", icon: "people" },
    { value: "directory",  label: "Volunteer Directory",  icon: "contacts" },
    { value: "account",    label: "Account",    icon: "manage_accounts" },
    { value: "admissions", label: "Admissions", icon: "how_to_reg" },
];

const ANSWER_TYPE_META: { value: AnswerType; label: string; icon: string; hint: string }[] = [
    { value: "rating", label: "Star rating",     icon: "star",          hint: "1–5 scale — great for quick sentiment" },
    { value: "emoji",  label: "Emoji scale",     icon: "mood",          hint: "😖 → 😍, playful & fast" },
    { value: "choice", label: "Multiple choice", icon: "checklist",     hint: "Pick one from your options" },
    { value: "text",   label: "Free text",       icon: "notes",         hint: "Open-ended written answer" },
];

function prettyCategory(cat: string): string {
    return PAGE_PRESETS.find((p) => p.value === cat)?.label || cat;
}

function categoryIcon(cat: string): string {
    return PAGE_PRESETS.find((p) => p.value === cat)?.icon || "tab";
}

function QuestionsTab() {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<QuestionForm>(BLANK_FORM);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth(`/api/v1/feedback/questions/all`);
            if (res.ok) setQuestions(await res.json());
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const startCreate = () => { setEditingId(null); setForm(BLANK_FORM); setShowModal(true); };
    const startEdit = (q: Question) => {
        setEditingId(q.id);
        setForm({
            page_category: q.page_category,
            prompt: q.prompt,
            answer_type: q.answer_type as AnswerType,
            options: q.options || [],
            is_active: q.is_active,
        });
        setShowModal(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            // On create, append to the end of the category so order is sensible.
            const catCount = questions.filter((q) => q.page_category === form.page_category).length;
            const body = {
                page_category: form.page_category.trim().toLowerCase(),
                prompt: form.prompt.trim(),
                answer_type: form.answer_type,
                options: form.answer_type === "choice" ? form.options : null,
                ...(editingId ? {} : { sort_order: catCount }),
                is_active: form.is_active,
            };
            const url = editingId ? `/api/v1/feedback/questions/${editingId}` : `/api/v1/feedback/questions`;
            const res = await fetchWithAuth(url, {
                method: editingId ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (res.ok) { setShowModal(false); load(); }
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: string) => {
        if (!confirm("Delete this question?")) return;
        const res = await fetchWithAuth(`/api/v1/feedback/questions/${id}`, { method: "DELETE" });
        if (res.ok) load();
    };

    const toggleActive = async (q: Question) => {
        const res = await fetchWithAuth(`/api/v1/feedback/questions/${q.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ is_active: !q.is_active }),
        });
        if (res.ok) load();
    };

    // Swap sort_order with the neighbour in the given direction.
    const reorder = async (cat: string, index: number, dir: -1 | 1) => {
        const qs = grouped[cat];
        const target = qs[index + dir];
        const current = qs[index];
        if (!target) return;
        await Promise.all([
            fetchWithAuth(`/api/v1/feedback/questions/${current.id}`, {
                method: "PUT", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sort_order: target.sort_order }),
            }),
            fetchWithAuth(`/api/v1/feedback/questions/${target.id}`, {
                method: "PUT", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sort_order: current.sort_order }),
            }),
        ]);
        load();
    };

    // Group by category (preset order first, then any custom categories).
    const grouped = questions.reduce<Record<string, Question[]>>((acc, q) => {
        (acc[q.page_category] ||= []).push(q);
        return acc;
    }, {});
    Object.values(grouped).forEach((qs) => qs.sort((a, b) => a.sort_order - b.sort_order));
    const presetOrder = PAGE_PRESETS.map((p) => p.value);
    const orderedCats = Object.keys(grouped).sort(
        (a, b) => (presetOrder.indexOf(a) + 1 || 99) - (presetOrder.indexOf(b) + 1 || 99)
    );

    return (
        <div className="space-y-5">
            <div className="flex justify-between items-center">
                <p className="text-sm text-[#666]">Curate the page-aware questions shown in the feedback pill.</p>
                <button
                    onClick={startCreate}
                    className="flex items-center gap-1.5 rounded-lg bg-[#003d45] px-4 py-2 text-sm font-bold text-white hover:bg-[#007a8a] transition-colors"
                >
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    New Question
                </button>
            </div>

            {loading ? (
                <p className="text-sm text-[#888]">Loading…</p>
            ) : orderedCats.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#cbd5e1] py-12 text-center">
                    <span className="material-symbols-outlined text-[40px] text-[#cbd5e1]">quiz</span>
                    <p className="mt-2 text-sm text-[#888]">No questions yet. Create your first one.</p>
                </div>
            ) : (
                orderedCats.map((cat) => (
                    <div key={cat} className="space-y-2">
                        <p className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#007a8a]">
                            <span className="material-symbols-outlined text-[16px]">{categoryIcon(cat)}</span>
                            {prettyCategory(cat)}
                        </p>
                        {grouped[cat].map((q, i) => {
                            const meta = ANSWER_TYPE_META.find((m) => m.value === q.answer_type);
                            return (
                                <div key={q.id} className="flex items-center gap-3 rounded-lg border border-[#e2e8f0] bg-white px-3 py-3">
                                    {/* Reorder */}
                                    <div className="flex flex-col">
                                        <button
                                            onClick={() => reorder(cat, i, -1)}
                                            disabled={i === 0}
                                            className="text-[#bbb] hover:text-[#007a8a] disabled:opacity-30 disabled:hover:text-[#bbb]"
                                            title="Move up"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">keyboard_arrow_up</span>
                                        </button>
                                        <button
                                            onClick={() => reorder(cat, i, 1)}
                                            disabled={i === grouped[cat].length - 1}
                                            className="text-[#bbb] hover:text-[#007a8a] disabled:opacity-30 disabled:hover:text-[#bbb]"
                                            title="Move down"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">keyboard_arrow_down</span>
                                        </button>
                                    </div>

                                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#f0f2f4] text-[#007a8a]" title={meta?.label}>
                                        <span className="material-symbols-outlined text-[18px]">{meta?.icon || "help"}</span>
                                    </span>

                                    <div className="min-w-0 flex-1">
                                        <p className={`text-sm font-semibold ${q.is_active ? "text-[#1a1a1a]" : "text-[#aaa] line-through"}`}>
                                            {q.prompt}
                                        </p>
                                        <p className="text-[11px] text-[#888]">
                                            {meta?.label || q.answer_type}
                                            {q.answer_type === "choice" && q.options && ` · ${q.options.length} options`}
                                        </p>
                                    </div>

                                    <div className="flex flex-shrink-0 items-center gap-1">
                                        <button
                                            onClick={() => toggleActive(q)}
                                            className={`rounded-lg px-2 py-1 text-[10px] font-black uppercase tracking-wider transition-colors ${
                                                q.is_active
                                                    ? "bg-[#e6f4f1] text-[#047857] hover:bg-[#d1fae5]"
                                                    : "bg-[#f0f2f4] text-[#999] hover:bg-[#e2e8f0]"
                                            }`}
                                            title={q.is_active ? "Active — click to hide" : "Hidden — click to show"}
                                        >
                                            {q.is_active ? "Live" : "Off"}
                                        </button>
                                        <button onClick={() => startEdit(q)} className="rounded-lg p-1.5 text-[#666] hover:bg-[#f0f2f4]" title="Edit">
                                            <span className="material-symbols-outlined text-[18px]">edit</span>
                                        </button>
                                        <button onClick={() => remove(q.id)} className="rounded-lg p-1.5 text-[#666] hover:bg-red-50 hover:text-[#dc2626]" title="Delete">
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))
            )}

            {showModal && (
                <QuestionEditorModal
                    form={form}
                    setForm={setForm}
                    editing={!!editingId}
                    saving={saving}
                    onSave={save}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
}

// ── Question editor modal (with live preview) ───────────────────────────────

function QuestionEditorModal({ form, setForm, editing, saving, onSave, onClose }: {
    form: QuestionForm;
    setForm: (f: QuestionForm) => void;
    editing: boolean;
    saving: boolean;
    onSave: () => void;
    onClose: () => void;
}) {
    const [customCategory, setCustomCategory] = useState(
        !PAGE_PRESETS.some((p) => p.value === form.page_category)
    );
    const [optionDraft, setOptionDraft] = useState("");

    const valid =
        form.prompt.trim() !== "" &&
        form.page_category.trim() !== "" &&
        (form.answer_type !== "choice" || form.options.length >= 2);

    const addOption = () => {
        const v = optionDraft.trim();
        if (v && !form.options.includes(v)) {
            setForm({ ...form, options: [...form.options, v] });
        }
        setOptionDraft("");
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between bg-[#003d45] px-6 py-4">
                    <h3 className="text-base font-black uppercase tracking-wider text-white">
                        {editing ? "Edit Question" : "New Question"}
                    </h3>
                    <button onClick={onClose} className="text-white/70 hover:text-white">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <div className="grid flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[1.3fr_1fr]">
                    {/* ── Editor ── */}
                    <div className="space-y-5 p-6">
                        {/* Page */}
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-wider text-[#666]">Which page?</label>
                            {!customCategory ? (
                                <div className="flex flex-wrap gap-2">
                                    {PAGE_PRESETS.map((p) => (
                                        <button
                                            key={p.value}
                                            onClick={() => setForm({ ...form, page_category: p.value })}
                                            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                form.page_category === p.value
                                                    ? "border-[#007a8a] bg-[#007a8a] text-white"
                                                    : "border-[#e2e8f0] bg-white text-[#555] hover:border-[#007a8a]"
                                            }`}
                                        >
                                            <span className="material-symbols-outlined text-[15px]">{p.icon}</span>
                                            {p.label}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => { setCustomCategory(true); setForm({ ...form, page_category: "" }); }}
                                        className="rounded-full border border-dashed border-[#cbd5e1] px-3 py-1.5 text-xs font-semibold text-[#888] hover:border-[#007a8a]"
                                    >
                                        + Custom…
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <input
                                        autoFocus
                                        value={form.page_category}
                                        onChange={(e) => setForm({ ...form, page_category: e.target.value })}
                                        placeholder="e.g. presenters"
                                        className="w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm"
                                    />
                                    <button
                                        onClick={() => { setCustomCategory(false); setForm({ ...form, page_category: "general" }); }}
                                        className="text-xs font-semibold text-[#888] hover:text-[#007a8a]"
                                    >
                                        Presets
                                    </button>
                                </div>
                            )}
                            <p className="text-[11px] text-[#999]">The page's first URL segment. “Every page” is shown wherever a page has no questions of its own.</p>
                        </div>

                        {/* Prompt */}
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-wider text-[#666]">Your question</label>
                            <textarea
                                autoFocus={!customCategory}
                                value={form.prompt}
                                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                                rows={2}
                                placeholder="e.g. How easy was it to find what you needed?"
                                className="w-full resize-none rounded-lg border border-[#e2e8f0] px-3 py-2.5 text-sm focus:border-[#007a8a] focus:outline-none focus:ring-1 focus:ring-[#007a8a]"
                            />
                        </div>

                        {/* Answer type */}
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-wider text-[#666]">How do people answer?</label>
                            <div className="grid grid-cols-2 gap-2">
                                {ANSWER_TYPE_META.map((t) => (
                                    <button
                                        key={t.value}
                                        onClick={() => setForm({ ...form, answer_type: t.value })}
                                        className={`flex items-start gap-2 rounded-xl border p-3 text-left transition-colors ${
                                            form.answer_type === t.value
                                                ? "border-[#007a8a] bg-[#007a8a]/5 ring-1 ring-[#007a8a]"
                                                : "border-[#e2e8f0] hover:border-[#007a8a]/50"
                                        }`}
                                    >
                                        <span className="material-symbols-outlined text-[20px] text-[#007a8a]">{t.icon}</span>
                                        <span className="min-w-0">
                                            <span className="block text-sm font-bold text-[#1a1a1a]">{t.label}</span>
                                            <span className="block text-[11px] leading-tight text-[#888]">{t.hint}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Choice options */}
                        {form.answer_type === "choice" && (
                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-wider text-[#666]">Answer options</label>
                                <div className="flex flex-wrap gap-2">
                                    {form.options.map((opt) => (
                                        <span key={opt} className="flex items-center gap-1 rounded-full bg-[#f0f2f4] py-1 pl-3 pr-1.5 text-xs font-semibold text-[#444]">
                                            {opt}
                                            <button
                                                onClick={() => setForm({ ...form, options: form.options.filter((o) => o !== opt) })}
                                                className="flex h-4 w-4 items-center justify-center rounded-full text-[#999] hover:bg-[#dc2626] hover:text-white"
                                            >
                                                <span className="material-symbols-outlined text-[12px]">close</span>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        value={optionDraft}
                                        onChange={(e) => setOptionDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                                        placeholder="Type an option, press Enter"
                                        className="flex-1 rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm"
                                    />
                                    <button onClick={addOption} className="rounded-lg bg-[#f0f2f4] px-3 text-sm font-semibold text-[#333] hover:bg-[#e2e8f0]">
                                        Add
                                    </button>
                                </div>
                                {form.options.length < 2 && (
                                    <p className="text-[11px] text-[#dc2626]">Add at least two options.</p>
                                )}
                            </div>
                        )}

                        {/* Active */}
                        <label className="flex items-center gap-2.5 pt-1">
                            <input
                                type="checkbox"
                                checked={form.is_active}
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                className="h-4 w-4 rounded text-[#007a8a]"
                            />
                            <span className="text-sm font-medium text-[#444]">Show this question to users right away</span>
                        </label>
                    </div>

                    {/* ── Live preview ── */}
                    <div className="border-t border-[#e2e8f0] bg-[#f8fafc] p-6 md:border-l md:border-t-0">
                        <p className="mb-3 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#888]">
                            <span className="material-symbols-outlined text-[14px]">visibility</span>
                            Live preview
                        </p>
                        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4 shadow-sm">
                            <QuestionPreview form={form} />
                        </div>
                        <p className="mt-3 text-[11px] text-[#999]">This is exactly how the question appears in the feedback pill.</p>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 border-t border-[#e2e8f0] px-6 py-4">
                    <button onClick={onClose} className="rounded-lg bg-[#f0f2f4] px-4 py-2 text-sm font-semibold text-[#333] hover:bg-[#e2e8f0]">
                        Cancel
                    </button>
                    <button
                        onClick={onSave}
                        disabled={!valid || saving}
                        className="rounded-lg bg-[#003d45] px-5 py-2 text-sm font-bold text-white hover:bg-[#007a8a] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? "Saving…" : editing ? "Save changes" : "Create question"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Renders a question the same way the user-facing pill does (read-only preview).
function QuestionPreview({ form }: { form: QuestionForm }) {
    const EMOJIS = ["😖", "🙁", "😐", "🙂", "😍"];
    return (
        <div className="space-y-2">
            <p className="text-sm font-semibold text-[#1a1a1a]">
                {form.prompt || <span className="text-[#bbb]">Your question will appear here…</span>}
            </p>
            {form.answer_type === "rating" && (
                <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <span key={n} className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f0f2f4] text-sm font-bold text-[#666]">{n}</span>
                    ))}
                </div>
            )}
            {form.answer_type === "emoji" && (
                <div className="flex gap-1.5">
                    {EMOJIS.map((e) => (
                        <span key={e} className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#f0f2f4] text-xl">{e}</span>
                    ))}
                </div>
            )}
            {form.answer_type === "choice" && (
                <div className="flex flex-wrap gap-2">
                    {form.options.length === 0 ? (
                        <span className="text-xs text-[#bbb]">Add options to preview them…</span>
                    ) : form.options.map((o) => (
                        <span key={o} className="rounded-full bg-[#f0f2f4] px-3 py-1.5 text-xs font-semibold text-[#666]">{o}</span>
                    ))}
                </div>
            )}
            {form.answer_type === "text" && (
                <div className="h-14 w-full rounded-lg border border-[#e2e8f0] bg-[#fafbfc] px-3 py-2 text-sm text-[#bbb]">Your thoughts…</div>
            )}
        </div>
    );
}
